import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { lazy, Suspense, useState } from "react";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { RoleProtectedRoute } from "@/components/RoleProtectedRoute";
import { DebugBar } from "@/components/DebugBar";
import { AuthDebugger } from "@/components/AuthDebugger";
import { HeadlessModeProvider } from "@/contexts/HeadlessModeContext";
import { DebugUserLevelProvider } from "@/contexts/DebugUserLevelContext";
import PWAUpdateNotification from "@/components/PWAUpdateNotification";
import { useConnectionHealth } from "@/hooks/useConnectionHealth";
import * as Sentry from "@sentry/react";

/**
 * Lazy-loaded route components for code splitting.
 * 
 * Components are loaded on-demand when their routes are accessed,
 * reducing the initial bundle size and improving load time.
 */
const Index = lazy(() => import("./pages/Index"));
const Room = lazy(() => import("./pages/Room"));
const Auth = lazy(() => import("./pages/Auth"));
const CorporateSignup = lazy(() => import("./pages/CorporateSignup"));
const UploadStory = lazy(() => import("./pages/UploadStory"));
const MyStories = lazy(() => import("./pages/MyStories"));
const EditStory = lazy(() => import("./pages/EditStory"));
const PageEditor = lazy(() => import("./pages/PageEditor"));
const CorporateAdmin = lazy(() => import("./pages/CorporateAdmin"));
const DatabaseTest = lazy(() => import("./pages/DatabaseTest"));
const AllBooks = lazy(() => import("./pages/AllBooks"));
const NotFound = lazy(() => import("./pages/NotFound"));

/**
 * React Query client instance for managing server state and caching.
 */
const queryClient = new QueryClient();

/**
 * Main application component.
 * 
 * Sets up:
 * - React Query provider for data fetching and caching
 * - Tooltip provider for tooltip components
 * - Toast notification systems (Toaster and Sonner)
 * - React Router for navigation
 * - Lazy-loaded route components with Suspense fallback
 * - Protected routes that require authentication
 * 
 * The Suspense fallback shows a loading spinner while route components are being loaded.
 */
const App = () => {
  const [isAuthDebuggerOpen, setIsAuthDebuggerOpen] = useState(false);

  // Monitor connection health to prevent idle connection issues
  useConnectionHealth(true, 5 * 60 * 1000); // Check every 5 minutes

  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
      <HeadlessModeProvider>
        <DebugUserLevelProvider>
          <Toaster />
          <Sonner />
        <BrowserRouter
          future={{
            v7_startTransition: true,
            v7_relativeSplatPath: true,
          }}
        >
          <Suspense fallback={
            <div className="flex items-center justify-center min-h-screen bg-background">
              <div className="text-center space-y-4">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
                <p className="text-muted-foreground">Loading...</p>
              </div>
            </div>
          }>
            <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/auth" element={<Auth />} />
              <Route path="/corporate/signup" element={<CorporateSignup />} />
              <Route path="/room/:id" element={
                <ProtectedRoute>
                  <Room />
                </ProtectedRoute>
              } />
              <Route path="/upload" element={
                <ProtectedRoute>
                  <UploadStory />
                </ProtectedRoute>
              } />
              <Route path="/my-stories" element={
                <RoleProtectedRoute allowedLevels={[4, 5, 6]}>
                  <MyStories />
                </RoleProtectedRoute>
              } />
              <Route path="/edit-story/:id" element={
                <ProtectedRoute>
                  <EditStory />
                </ProtectedRoute>
              } />
              <Route path="/edit-page/:bookId/:pageId" element={
                <ProtectedRoute>
                  <PageEditor />
                </ProtectedRoute>
              } />
              <Route path="/corporate/admin" element={
                <ProtectedRoute>
                  <CorporateAdmin />
                </ProtectedRoute>
              } />
              <Route path="/books" element={<AllBooks />} />
              <Route path="/db-test" element={<DatabaseTest />} />
              {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
          <DebugBar onOpenAuthDebugger={() => setIsAuthDebuggerOpen(true)} />
          <PWAUpdateNotification />
        </BrowserRouter>
        <AuthDebugger isOpen={isAuthDebuggerOpen} onClose={() => setIsAuthDebuggerOpen(false)} />
        </DebugUserLevelProvider>
      </HeadlessModeProvider>
    </TooltipProvider>
  </QueryClientProvider>
  );
};

// Wrap App with Sentry Error Boundary to catch and report React errors
const AppWithErrorBoundary = Sentry.withErrorBoundary(App, {
  fallback: ({ error }) => (
    <div className="flex items-center justify-center min-h-screen bg-background">
      <div className="text-center space-y-4 p-8">
        <h1 className="text-2xl font-bold text-destructive">Something went wrong</h1>
        <p className="text-muted-foreground">An error occurred. Please refresh the page.</p>
        <button
          onClick={() => window.location.reload()}
          className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90"
        >
          Refresh Page
        </button>
      </div>
    </div>
  ),
  showDialog: false,
});

export default AppWithErrorBoundary;
